import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../api/use-claim";
import { ClaimValidationCTA } from "./states-cta/claim-validation-cta";
import { EmailConversationCTA } from "./states-cta/email-conversation-cta";
import { ForceArchivePanelBody } from "./states-cta/force-archive-panel";
import { FormalNoticeCTA } from "./states-cta/formal-notice-cta";
import { GeneratePleadingBriefCTA } from "./states-cta/generate-pleading-brief-cta";
import { GenerateStampedExhibitsCTA } from "./states-cta/generate-stamped-exhibits-cta";
import { InitProcedureCTA } from "./states-cta/init-procedure-cta";
import { LegalSubpoenaCTA } from "./states-cta/legal-subpoena-cta";
import { MergeAssignationCTA } from "./states-cta/merge-assignation-cta";
import { NumeroRGCTA } from "./states-cta/numero-rg-cta";
import { OnePagerCTA } from "./states-cta/one-pager-cta";
import { ReceivablesContractsCTA } from "./states-cta/receivables-contracts-cta";
import { RegenerateLegalSubpoenaCTA } from "./states-cta/regenerate-legal-subpoena-cta";
import { StartClaimCTA } from "./states-cta/start-claim-cta";
import { TravelAgencySignatureCTA } from "./states-cta/travel-agency-signature-cta";
import { UploadAssignationToReview } from "./states-cta/upload-assignation-to-review";

/**
 * Port of the « State dependant » CTA block of Overview.vue.
 * Each CTA keeps the exact original display condition (Overview.vue lines 209-338).
 */
export function StateCtas({ claim }: { claim: ClaimWithRelations }) {
  const { can } = useCurrentUser();

  const stateType = claim.claimState?.type;
  const hasOnePager = Boolean(
    claim.claimDocuments.find(
      ({ file: { name }, documentType: { type } }) =>
        type === "ONE_PAGER" || (name || "").includes("One Pager"),
    ),
  );
  const hasEmailConversation = claim.claimDocuments?.some(
    ({ documentType: { type } }) => type === "EMAIL_CONVERSATION",
  );
  const hasSubpoenaToReview = Boolean(
    claim.claimDocuments.find((d) => d.documentType.type === "LEGAL_SUBPOENA_TO_REVIEW"),
  );

  return (
    <>
      {stateType === "CLAIM_VALIDATION.WAIT_FOR_ELIGIBILITY_CHECK" && can(UPCLAIM_ROLES) ? (
        <ClaimValidationCTA claim={claim} />
      ) : null}

      {(stateType === "CLAIM_VALIDATION.CHOICE_OF_CONTACT_MODE" ||
        stateType === "CLAIM_VALIDATION.WAIT_FOR_PASSENGER_YOUSIGN_SIGNATURE" ||
        stateType === "CLAIM_VALIDATION.WAIT_FOR_DOCUMENTS_CREATION") &&
      can(UPCLAIM_ROLES) ? (
        <ReceivablesContractsCTA claim={claim} />
      ) : null}

      {stateType === "CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_TRAVEL_AGENCY" && can(UPCLAIM_ROLES) ? (
        <TravelAgencySignatureCTA claim={claim} />
      ) : null}

      {stateType === "UPCLAIM.WAIT_FOR_1ST_EMAIL_SEND" && can(UPCLAIM_ROLES) ? (
        <StartClaimCTA claim={claim} />
      ) : null}

      {stateType === "UPCLAIM.WAIT_SENDS_MED" && can(UPCLAIM_ROLES) ? (
        <FormalNoticeCTA claim={claim} />
      ) : null}

      {stateType?.startsWith("COURT") && !claim.procedure && can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <InitProcedureCTA claim={claim} />
      ) : null}

      {(stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_ASSIGNATION_GENERATION" ||
        stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_GROUP") &&
      !hasSubpoenaToReview &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <UploadAssignationToReview claimId={claim.id} />
      ) : null}

      {!hasEmailConversation &&
      claim.procedure &&
      stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE" &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <EmailConversationCTA claim={claim} />
      ) : null}

      {!hasOnePager &&
      hasEmailConversation &&
      claim.procedure &&
      stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE" &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <OnePagerCTA claim={claim} />
      ) : null}

      {claim.procedure &&
      !claim.legalSubpoena &&
      ((hasOnePager && stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE") ||
        stateType === "COURT.WAIT_FOR_ESCP_FORM_A") &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <LegalSubpoenaCTA claim={claim} />
      ) : null}

      {claim.legalSubpoena &&
      (stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE" ||
        stateType === "COURT.WAIT_FOR_ESCP_FORM_A") &&
      can(UPCLAIM_ROLES) ? (
        <RegenerateLegalSubpoenaCTA claim={claim} />
      ) : null}

      {claim.procedure &&
      stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_HEARING" &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <GeneratePleadingBriefCTA claim={claim} />
      ) : null}

      {claim.procedure &&
      (stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_CALENDAR_HEARING" ||
        stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_ENROLMENT") &&
      !claim.procedure.legalSubpoenaDocumentsTransmittedToAdversaryAt &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <GenerateStampedExhibitsCTA claim={claim} />
      ) : null}

      {claim.procedure &&
      claim.legalSubpoena &&
      stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_BAILLIF_TRANSMISSION" &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <MergeAssignationCTA claim={claim} />
      ) : null}

      {!claim.procedure &&
      claim.claimEvents.some((e) => e.eventType.type === "COURT.PROCEDURE_REPL") &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <InitProcedureCTA claim={claim} />
      ) : null}

      {!claim.procedure?.identifier &&
      (stateType === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_CALENDAR_HEARING" ||
        claim.procedure?.procedureType === "ESCP") &&
      can([...UPCLAIM_ROLES, "LAWYER"]) ? (
        <NumeroRGCTA claim={claim} />
      ) : null}
    </>
  );
}

/**
 * Wraps StateCtas in a clearly labeled "Prochaine étape" card. StateCtas has no single
 * "nothing to show" signal (15 independent conditions), so emptiness is detected post-render
 * via the content wrapper's child count rather than duplicating all the conditions here.
 */
export function NextStepCard({ claim }: { claim: ClaimWithRelations }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasContent, setHasContent] = useState(true);

  // Detect emptiness by watching the content wrapper's children (StateCtas has ~15 independent
  // conditions). A MutationObserver updates only when the child list actually changes — never on every
  // render — so it can't feed a render loop the way a dependency-less measure effect can.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const update = () => setHasContent(content.childElementCount > 0);
    update();
    const observer = new MutationObserver(update);
    observer.observe(content, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className={cn("h-fit rounded-lg border bg-white shadow-sm", !hasContent && "hidden")}>
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <ArrowRight className="h-4 w-4 text-blue-600" />
        <h3 className="text-sm font-semibold text-gray-900">Prochaine étape</h3>
      </div>
      <div ref={contentRef} className="p-4">
        <StateCtas claim={claim} />
      </div>
    </div>
  );
}

/** Port of ForceArchivePanel.vue — keeps the Overview.vue display condition. */
export function ForceArchivePanel({ claim }: { claim: ClaimWithRelations }) {
  const stateType = claim.claimState?.type;
  if (
    stateType !== "CLAIM_VALIDATION.WAIT_FOR_PAX_EMAIL_FROM_TRAVEL_AGENCY" &&
    stateType !== "CLAIM_VALIDATION.WAIT_FOR_PAX_EMAIL_FROM_ENTERPRISE"
  ) {
    return null;
  }

  return <ForceArchivePanelBody claim={claim} />;
}
